import { useMutation, useQuery } from '@apollo/client/react/index.js';
import {
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassConfirmDialog,
  GlassEmptyState,
  GlassInput,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
} from '@merritrac/shared/components';
import { ArrowLeft, Building, Eye, Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  CREATE_CLIENT,
  DELETE_CLIENT,
  GET_CLIENTS,
  GET_PROPERTIES,
  UPDATE_CLIENT,
} from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

/* ── GraphQL response types ── */

interface ClientNode {
  clientCode: string;
  clientName: string | null;
  contact: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  status: string | null;
  type: string | null;
  inactive: number | null;
  onHold: number | null;
}

interface ClientEdge {
  node: ClientNode;
}

interface GetClientsData {
  clients: {
    edges: ClientEdge[];
  };
}

/* ── Form state ── */

interface ClientForm {
  clientCode: string;
  clientName: string;
  contact: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  notes: string;
}

const emptyForm: ClientForm = {
  clientCode: '',
  clientName: '',
  contact: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  email: '',
  notes: '',
};

/* ── Page ── */

export function ClientDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const isNew = id === 'new' || id === undefined;
  const clientCode = isNew ? '' : decodeURIComponent(id);

  const { data: rawData, loading } = useQuery<GetClientsData>(GET_CLIENTS, {
    variables: { first: 200 },
    skip: isNew,
  });

  // Find the specific client from the connection
  const existingClient: ClientNode | undefined = rawData?.clients.edges
    .map((e) => e.node)
    .find((n) => n.clientCode === clientCode);

  const [form, setForm] = useState<ClientForm>(emptyForm);
  const [isDirty, setIsDirty] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // Fetch properties for this client
  interface PropertyNode {
    id: number;
    name: string;
    type: string | null;
    city: string | null;
    state: string | null;
    totalUnits: number | null;
    active: number | null;
  }
  interface GetPropertiesData {
    properties: {
      edges: { node: PropertyNode }[];
    };
  }
  const { data: propertiesData } = useQuery<GetPropertiesData>(GET_PROPERTIES, {
    variables: { first: 100, clientCode },
    skip: isNew || clientCode === '',
  });
  const properties: PropertyNode[] = propertiesData?.properties.edges.map((e) => e.node) ?? [];

  const [createClient, { loading: creating }] = useMutation(CREATE_CLIENT, {
    refetchQueries: [{ query: GET_CLIENTS, variables: { first: 200 } }],
  });
  const [updateClient, { loading: updating }] = useMutation(UPDATE_CLIENT, {
    refetchQueries: [{ query: GET_CLIENTS, variables: { first: 200 } }],
  });

  // Sync form when data loads
  useEffect(() => {
    if (existingClient !== undefined) {
      setForm({
        clientCode: existingClient.clientCode,
        clientName: existingClient.clientName ?? '',
        contact: existingClient.contact ?? '',
        address: existingClient.address ?? '',
        city: existingClient.city ?? '',
        state: existingClient.state ?? '',
        zip: existingClient.zip ?? '',
        phone: existingClient.phone ?? '',
        email: existingClient.email ?? '',
        notes: existingClient.notes ?? '',
      });
      setIsDirty(false);
    }
  }, [existingClient?.clientCode]);

  function handleChange(field: keyof ClientForm, value: string): void {
    setForm((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  }

  function handleSave(e: React.SyntheticEvent): void {
    e.preventDefault();
    if (form.clientCode.trim() === '') {
      return;
    }

    if (isNew) {
      void createClient({
        variables: {
          clientCode: form.clientCode,
          clientName: form.clientName !== '' ? form.clientName : undefined,
          contact: form.contact !== '' ? form.contact : undefined,
          address: form.address !== '' ? form.address : undefined,
          city: form.city !== '' ? form.city : undefined,
          state: form.state !== '' ? form.state : undefined,
          zip: form.zip !== '' ? form.zip : undefined,
          phone: form.phone !== '' ? form.phone : undefined,
          email: form.email !== '' ? form.email : undefined,
          notes: form.notes !== '' ? form.notes : undefined,
        },
      }).then(() => {
        void navigate(`/clients/${encodeURIComponent(form.clientCode)}`);
      });
    } else {
      void updateClient({
        variables: {
          clientCode,
          clientName: form.clientName !== '' ? form.clientName : undefined,
          contact: form.contact !== '' ? form.contact : undefined,
          address: form.address !== '' ? form.address : undefined,
          city: form.city !== '' ? form.city : undefined,
          state: form.state !== '' ? form.state : undefined,
          zip: form.zip !== '' ? form.zip : undefined,
          phone: form.phone !== '' ? form.phone : undefined,
          email: form.email !== '' ? form.email : undefined,
          notes: form.notes !== '' ? form.notes : undefined,
        },
      }).then(() => {
        setIsDirty(false);
      });
    }
  }

  function handleBack(): void {
    void navigate('/clients');
  }

  const saving = creating || updating;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <GlassButton variant="ghost" size="sm" onClick={handleBack} aria-label="Back to clients">
            <ArrowLeft size={14} />
          </GlassButton>
          <div>
            <h1 className="text-xl font-semibold text-text">
              {isNew ? 'New Client' : form.clientName !== '' ? form.clientName : clientCode}
            </h1>
            {!isNew && (
              <p className="text-sm text-text-secondary mt-0.5">Client Code: {clientCode}</p>
            )}
            {!isNew && existingClient !== undefined && (
              <div className="flex items-center gap-2 flex-wrap mt-2">
                {existingClient.status !== null && existingClient.status !== '' && (
                  <GlassBadge
                    variant={
                      existingClient.status === 'CA'
                        ? 'success'
                        : existingClient.status === 'PR'
                          ? 'info'
                          : existingClient.status === 'CX'
                            ? 'danger'
                            : existingClient.status === 'RP'
                              ? 'warning'
                              : 'default'
                    }
                  >
                    {existingClient.status === 'CA'
                      ? 'Active'
                      : existingClient.status === 'PR'
                        ? 'Prospect'
                        : existingClient.status === 'CX'
                          ? 'Cancelled'
                          : existingClient.status === 'RP'
                            ? 'Reporting'
                            : existingClient.status}
                  </GlassBadge>
                )}
                {existingClient.type !== null && existingClient.type !== '' && (
                  <GlassBadge variant="default">
                    {existingClient.type === 'CC'
                      ? 'Condo Corp'
                      : existingClient.type === 'RN'
                        ? 'Rental'
                        : existingClient.type === 'MC'
                          ? 'Mgmt Co'
                          : existingClient.type === 'CO'
                            ? 'Co-op'
                            : existingClient.type}
                  </GlassBadge>
                )}
                {existingClient.inactive === 1 && (
                  <GlassBadge variant="warning">Inactive</GlassBadge>
                )}
                {existingClient.onHold === 1 && <GlassBadge variant="warning">On Hold</GlassBadge>}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <GlassButton
              variant="ghost"
              size="sm"
              className="text-danger hover:text-danger hover:bg-danger-soft"
              onClick={() => {
                setShowDelete(true);
              }}
            >
              <Trash2 size={14} />
              Delete
            </GlassButton>
          )}
          <GlassButton
            variant="primary"
            size="md"
            onClick={handleSave}
            disabled={saving || !isDirty}
          >
            <Save size={14} />
            {saving ? 'Saving…' : 'Save'}
          </GlassButton>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-text-secondary">Loading…</div>
      ) : (
        <form onSubmit={handleSave}>
          {/* Client Info */}
          <GlassCard>
            <GlassCardHeader>
              <h2 className="text-base font-semibold text-text">Client Information</h2>
            </GlassCardHeader>
            <GlassCardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <GlassInput
                  label="Client Code"
                  value={form.clientCode}
                  onChange={(e) => {
                    handleChange('clientCode', e.target.value);
                  }}
                  disabled={!isNew}
                  required
                  placeholder="e.g. ACME001"
                />
                <GlassInput
                  label="Client Name"
                  value={form.clientName}
                  onChange={(e) => {
                    handleChange('clientName', e.target.value);
                  }}
                  placeholder="Company or client name"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <GlassInput
                  label="Contact Person"
                  value={form.contact}
                  onChange={(e) => {
                    handleChange('contact', e.target.value);
                  }}
                  placeholder="Primary contact"
                />
                <GlassInput
                  label="Phone"
                  value={form.phone}
                  onChange={(e) => {
                    handleChange('phone', e.target.value);
                  }}
                  placeholder="(555) 123-4567"
                />
              </div>
              <GlassInput
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => {
                  handleChange('email', e.target.value);
                }}
                placeholder="contact@company.com"
              />
            </GlassCardContent>
          </GlassCard>

          {/* Address */}
          <GlassCard className="mt-6">
            <GlassCardHeader>
              <h2 className="text-base font-semibold text-text">Address</h2>
            </GlassCardHeader>
            <GlassCardContent className="space-y-4">
              <GlassInput
                label="Street Address"
                value={form.address}
                onChange={(e) => {
                  handleChange('address', e.target.value);
                }}
                placeholder="123 Main St"
              />
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <GlassInput
                  label="City"
                  value={form.city}
                  onChange={(e) => {
                    handleChange('city', e.target.value);
                  }}
                  placeholder="City"
                />
                <GlassInput
                  label="State"
                  value={form.state}
                  onChange={(e) => {
                    handleChange('state', e.target.value);
                  }}
                  placeholder="ST"
                />
                <GlassInput
                  label="ZIP Code"
                  value={form.zip}
                  onChange={(e) => {
                    handleChange('zip', e.target.value);
                  }}
                  placeholder="12345"
                />
              </div>
            </GlassCardContent>
          </GlassCard>

          {/* Notes */}
          <GlassCard className="mt-6">
            <GlassCardHeader>
              <h2 className="text-base font-semibold text-text">Notes</h2>
            </GlassCardHeader>
            <GlassCardContent>
              <textarea
                value={form.notes}
                onChange={(e) => {
                  handleChange('notes', e.target.value);
                }}
                rows={4}
                placeholder="Additional notes about this client…"
                className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
              />
            </GlassCardContent>
          </GlassCard>
        </form>
      )}

      {/* Properties section (only when editing an existing client) */}
      {!isNew && !loading && (
        <GlassCard>
          <GlassCardHeader className="flex flex-row items-center justify-between">
            <h2 className="text-base font-semibold text-text">Properties</h2>
            <GlassButton
              variant="secondary"
              size="sm"
              onClick={() => {
                void navigate(`/properties/new?clientCode=${encodeURIComponent(clientCode)}`);
              }}
            >
              <Plus size={14} />
              Add Property
            </GlassButton>
          </GlassCardHeader>
          {properties.length === 0 ? (
            <div className="py-8">
              <GlassEmptyState
                icon={Building}
                title="No properties"
                description="This client has no properties yet. Add one to get started."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <GlassTable>
                <GlassTableHead>
                  <GlassTableRow>
                    <GlassTableHeaderCell>Property</GlassTableHeaderCell>
                    <GlassTableHeaderCell className="hidden sm:table-cell">
                      Type
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell className="hidden md:table-cell">
                      Location
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell className="hidden sm:table-cell">
                      Units
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell aria-label="Actions" />
                  </GlassTableRow>
                </GlassTableHead>
                <GlassTableBody>
                  {properties.map((prop) => (
                    <GlassTableRow
                      key={prop.id}
                      className="cursor-pointer"
                      onClick={() => {
                        void navigate(`/properties/${String(prop.id)}`);
                      }}
                    >
                      <GlassTableCell>
                        <span className="font-medium text-text">{prop.name}</span>
                      </GlassTableCell>
                      <GlassTableCell className="hidden sm:table-cell">
                        {prop.type !== null ? (
                          <GlassBadge variant="default">{prop.type.replace('_', ' ')}</GlassBadge>
                        ) : (
                          '—'
                        )}
                      </GlassTableCell>
                      <GlassTableCell className="hidden md:table-cell text-text-secondary">
                        {[prop.city, prop.state].filter(Boolean).join(', ') || '—'}
                      </GlassTableCell>
                      <GlassTableCell className="hidden sm:table-cell text-text-secondary">
                        {prop.totalUnits ?? '—'}
                      </GlassTableCell>
                      <GlassTableCell>
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          aria-label={`View ${prop.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            void navigate(`/properties/${String(prop.id)}`);
                          }}
                        >
                          <Eye size={14} />
                        </GlassButton>
                      </GlassTableCell>
                    </GlassTableRow>
                  ))}
                </GlassTableBody>
              </GlassTable>
            </div>
          )}
        </GlassCard>
      )}

      {/* Delete confirmation dialog */}
      <DeleteClientConfirm
        clientName={form.clientName !== '' ? form.clientName : clientCode}
        clientCode={clientCode}
        open={showDelete}
        onClose={() => {
          setShowDelete(false);
        }}
        onDeleted={() => {
          void navigate('/clients');
        }}
      />
    </div>
  );
}

/* ── Delete Confirm ── */

function DeleteClientConfirm({
  clientName,
  clientCode,
  open,
  onClose,
  onDeleted,
}: {
  clientName: string;
  clientCode: string;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}): ReactElement {
  const [deleteClient, { loading }] = useMutation(DELETE_CLIENT, {
    refetchQueries: [{ query: GET_CLIENTS, variables: { first: 200 } }],
  });

  return (
    <GlassConfirmDialog
      open={open}
      title="Delete Client"
      message={`Are you sure you want to delete "${clientName}"? This will permanently remove the client and cannot be undone.`}
      confirmLabel="Delete Client"
      variant="danger"
      loading={loading}
      onCancel={onClose}
      onConfirm={() => {
        void deleteClient({ variables: { clientCode } }).then(() => {
          onDeleted();
        });
      }}
    />
  );
}
